import type { ReactNode } from "react";
import clsx from "clsx";
import ResponsiveCDNImage from "../../../components/common/image/responsive-cdn-image";

export interface AdminStoreListItem {
  id: number;
  title: string;
  img?: string | null;
  address?: string | null;
}

export interface AdminStoreRowSingleProps {
  store: AdminStoreListItem;
  selected: boolean;
  onSelect: () => void;
  /** 이미지 컬럼 표시 여부 (기본: true) */
  showImage?: boolean;
  /** 주소 컬럼 표시 여부 (기본: true) */
  showAddress?: boolean;
  /** 셀 노드들 (주소 뒤에 렌더), 예: 가입상태, 가입일 등 */
  extraCells?: ReactNode;
}

export function AdminStoreRowSingle({
  store,
  selected,
  onSelect,
  showImage = true,
  showAddress = true,
  extraCells,
}: AdminStoreRowSingleProps) {
  return (
    <tr
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect();
        }
      }}
      className={clsx(
        "cursor-pointer transition-colors",
        selected ? "bg-blue-50 border-l-4 border-l-blue-600" : "hover:bg-gray-50"
      )}
    >
      <td className="px-4 py-3 text-gray-900">{store.id}</td>
      {showImage && (
        <td className="px-4 py-3">
          {store.img ? (
            <ResponsiveCDNImage
              src={store.img}
              alt={store.title}
              className="w-12 h-12 rounded object-cover"
              sizeRatio={0.12}
            />
          ) : (
            <div className="w-12 h-12 bg-gray-200 rounded" />
          )}
        </td>
      )}
      <td className="px-4 py-3 text-gray-900 truncate max-w-[120px] break-keep">
        {store.title}
      </td>
      {showAddress && (
        <td className="px-4 py-3 text-gray-600 text-xs max-w-[140px] break-keep">
          <span className="truncate block" title={store.address ?? undefined}>
            {store.address ?? "주소 없음"}
          </span>
        </td>
      )}
      {extraCells}
    </tr>
  );
}

export interface AdminStoreRowMultipleProps {
  store: AdminStoreListItem;
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
  /** 주소 컬럼 표시 여부 (기본: true) */
  showAddress?: boolean;
  /** 셀 노드들 (주소 뒤, 체크박스 앞에 렌더), 예: 좋아요, 매니저 등 */
  extraCells?: ReactNode;
}

export function AdminStoreRowMultiple({
  store,
  checked,
  onChange,
  disabled = false,
  showAddress = true,
  extraCells,
}: AdminStoreRowMultipleProps) {
  return (
    <tr
      role="button"
      tabIndex={disabled ? undefined : 0}
      onClick={() => !disabled && onChange()}
      onKeyDown={(e) => {
        if (!disabled && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onChange();
        }
      }}
      className={clsx(
        "transition-colors",
        disabled && "opacity-60 cursor-not-allowed",
        !disabled && "cursor-pointer hover:bg-gray-50"
      )}
    >
      <td className="px-4 py-3">
        {store.img ? (
          <ResponsiveCDNImage
            src={store.img}
            alt={store.title}
            className="w-12 h-12 rounded object-cover"
            sizeRatio={0.12}
          />
        ) : (
          <div className="w-12 h-12 bg-gray-200 rounded" />
        )}
      </td>
      <td className="px-4 py-3 text-gray-900 truncate max-w-[120px] break-keep">
        {store.title}
      </td>
      {showAddress && (
        <td className="px-4 py-3 text-gray-600 text-xs max-w-[140px] break-keep">
          <span className="truncate block" title={store.address ?? undefined}>
            {store.address ?? "주소 없음"}
          </span>
        </td>
      )}
      {extraCells}
      <td className="px-4 py-3">
        <input
          type="checkbox"
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          className="w-5 h-5 rounded border-gray-300"
          onClick={(e) => e.stopPropagation()}
        />
      </td>
    </tr>
  );
}

/** 단일 선택 테이블용 헤더 (ID, [이미지], 매장명, 주소 + extra 헤더) */
export interface AdminStoreTableHeaderSingleProps {
  /** 이미지 컬럼 표시 여부 (기본: true) */
  showImage?: boolean;
  /** 주소 컬럼 표시 여부 (기본: true) */
  showAddress?: boolean;
  extraHeaders?: ReactNode;
}

export function AdminStoreTableHeaderSingle({
  showImage = true,
  showAddress = true,
  extraHeaders,
}: AdminStoreTableHeaderSingleProps) {
  return (
    <tr>
      <th className="px-4 py-2 font-medium text-gray-700 break-keep">ID</th>
      {showImage && (
        <th className="px-4 py-2 font-medium text-gray-700 break-keep">이미지</th>
      )}
      <th className="px-4 py-2 font-medium text-gray-700 break-keep">매장명</th>
      {showAddress && (
        <th className="px-4 py-2 font-medium text-gray-700 break-keep">주소</th>
      )}
      {extraHeaders}
    </tr>
  );
}

/** 다중 선택 테이블용 헤더 (이미지, 매장명, 주소 + extra 헤더 + 선택) */
export interface AdminStoreTableHeaderMultipleProps {
  showAddress?: boolean;
  extraHeaders?: ReactNode;
}

export function AdminStoreTableHeaderMultiple({
  showAddress = true,
  extraHeaders,
}: AdminStoreTableHeaderMultipleProps) {
  return (
    <tr>
      <th className="px-4 py-2 font-medium text-gray-700 break-keep">이미지</th>
      <th className="px-4 py-2 font-medium text-gray-700 break-keep">매장명</th>
      {showAddress && (
        <th className="px-4 py-2 font-medium text-gray-700 break-keep">주소</th>
      )}
      {extraHeaders}
      <th className="px-4 py-2 font-medium text-gray-700 break-keep">선택</th>
    </tr>
  );
}
